import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Space, Card, Tabs, message, Spin, Input, Checkbox, Tooltip, Badge } from 'antd';
import { DownloadOutlined, EyeOutlined, FolderOutlined, LogoutOutlined, FileTextOutlined, SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const api = axios.create({ baseURL: '/api/portal' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = 'Bearer ' + token;
  return cfg;
});

// a proxima inspecao e a data mais proxima entre a externa e a interna
const maisProxima = (a, b) => [a, b].filter(Boolean).map((x) => String(x).substring(0, 10)).sort()[0] || null;
const STATUS_VENCIMENTO = (data) => {
  if (!data) return null;
  const d = dayjs(data.substring(0,10));
  const diff = d.diff(dayjs().startOf('day'), 'day');
  if (diff < 0) return { cor: 'red', label: 'Vencido ' + d.format('DD/MM/YYYY') };
  if (diff <= 60) return { cor: 'orange', label: d.format('DD/MM/YYYY') };
  return { cor: 'green', label: d.format('DD/MM/YYYY') };
};

function Portal() {
  const [laudos, setLaudos] = useState([]);
  const [porTag, setPorTag] = useState({});
  const [loading, setLoading] = useState(true);
  const [tagAberta, setTagAberta] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [art, setArt] = useState(null);
  const [arts, setArts] = useState([]);
  const [certificadosPadrao, setCertificadosPadrao] = useState([]);
  const [busca, setBusca] = useState('');
  const [baixando, setBaixando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const nome = localStorage.getItem('portal_nome') || '';

  useEffect(() => { carregarDados(); }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [rLaudos, rArt, rCerts, rArts] = await Promise.all([
        api.get('/laudos'),
        api.get('/art'),
        api.get('/certificados-padrao'),
        api.get('/arts').catch(() => ({ data: [] })),
      ]);
      setArts(Array.isArray(rArts.data) ? rArts.data : []);
      setLaudos(rLaudos.data.laudos || []);
      setPorTag(rLaudos.data.porTag || {});
      setArt(rArt.data);
      setCertificadosPadrao(rCerts.data || []);
    } catch(e) {
      if (e.response?.status === 401) { localStorage.clear(); window.location.href = '/portal/login'; }
      else message.error('Erro ao carregar documentos');
    } finally { setLoading(false); }
  };

  const abrirArquivo = async (caminho) => {
    const key = 'abrir_' + Date.now();
    message.loading({ content: 'Abrindo arquivo...', key, duration: 0 });
    try {
      const token = localStorage.getItem('portal_token');
      const r = await fetch('/api/portal/arquivo?caminho=' + encodeURIComponent(caminho), {
        headers: { Authorization: 'Bearer ' + token }
      });
      /* sessao-expirada-401 */ if (r.status === 401) { message.destroy(key); message.warning('Sua sessão expirou. Entre novamente.', 3); localStorage.removeItem('portal_token'); localStorage.removeItem('portal_nome'); setTimeout(() => { window.location.href = '/portal/login'; }, 1800); return; }
      if (!r.ok) throw new Error('Arquivo não encontrado');
      const blob = await r.blob();
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      message.destroy(key);
      window.open(url, '_blank');
    } catch { message.error({ content: 'Erro ao abrir arquivo', key }); }
  };

  const baixarArquivo = async (caminho, nome) => {
    const key = 'baixar_' + Date.now();
    message.loading({ content: 'Baixando arquivo...', key, duration: 0 });
    try {
      const token = localStorage.getItem('portal_token');
      const r = await fetch('/api/portal/arquivo?caminho=' + encodeURIComponent(caminho), {
        headers: { Authorization: 'Bearer ' + token }
      });
      /* sessao-expirada-401 */ if (r.status === 401) { message.destroy(key); message.warning('Sua sessão expirou. Entre novamente.', 3); localStorage.removeItem('portal_token'); localStorage.removeItem('portal_nome'); setTimeout(() => { window.location.href = '/portal/login'; }, 1800); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nome || caminho.split('/').pop();
      a.click();
      message.success({ content: 'Download concluído!', key });
    } catch { message.error({ content: 'Erro ao baixar arquivo', key }); }
  };

  const baixarEmMassa = async (arquivos) => {
    if (!arquivos || arquivos.length === 0) { message.warning('Selecione arquivos para baixar'); return; }
    setBaixando(true);
    setProgresso(0);
    const total = arquivos.length;
    // Baixar em blocos de 5
    for (let i = 0; i < total; i += 5) {
      const bloco = arquivos.slice(i, i + 5);
      await Promise.all(bloco.map(arq => baixarArquivo(arq.caminho, arq.nome)));
      setProgresso(Math.min(i + 5, total));
      if (i + 5 < total) await new Promise(r => setTimeout(r, 1000));
    }
    setBaixando(false);
    setProgresso(0);
    message.success('Download concluído!');
  };

  const sair = () => { localStorage.removeItem('portal_token'); localStorage.removeItem('portal_nome'); window.location.href = '/portal/login'; };

  const tags = Object.keys(porTag).sort();
  // Montar lista de todos os arquivos individualmente
  const todosArquivos = laudos.flatMap(l => (l.caminhos || []).map(arq => ({
    ...arq,
    laudo_id: l.id,
    tag: l.tag,
    data_inspecao: l.data_inspecao,
    // calibracao tem validade propria; os demais documentos seguem o laudo
    // livro, prontuario e anotacao nao vencem: ficam sem data
    proxima_inspecao_externa: /^(livro|prontu|anota)/i.test(String(arq.tipo || '')) ? null : (arq.vencimento || l.proxima_inspecao_externa),
    proxima_inspecao_interna: (arq.vencimento || /^(livro|prontu|anota)/i.test(String(arq.tipo || ''))) ? null : l.proxima_inspecao_interna,
    key: l.id + '_' + arq.caminho,
  })));
  const laudosFiltrados = laudos.filter(l => !busca || (l.tag||'').toLowerCase().includes(busca.toLowerCase()) || (l.tipo_laudo||'').toLowerCase().includes(busca.toLowerCase()));
  const arquivosFiltrados = todosArquivos.filter(a => !busca || (a.tag||'').toLowerCase().includes(busca.toLowerCase()) || (a.tipo||'').toLowerCase().includes(busca.toLowerCase()));

  const colunasArquivos = [
    { title: 'TAG', dataIndex: 'tag', width: 80, render: v => <Tag color="blue">{v}</Tag> },
    { title: 'Documento', dataIndex: 'tipo', ellipsis: true, render: v => { const tipos = { inspecao: 'Laudo de Inspeção', cal_valvula: 'Calib. Válvula de Seg.', cal_manometro: 'Calib. Manômetro', cal_pressostato: 'Calib. Pressostato', cal_transdutor: 'Calib. Transdutor', cal_pneu: 'Calib. Calibrador de Pneu', cal_termometro: 'Calib. Termômetro', prontuario: 'Prontuário', livro: 'Livro de Registro' }; return tipos[v] || v || '-'; } },
    { title: 'Data inspeção', dataIndex: 'data_inspecao', width: 110, render: v => v ? dayjs(v.substring(0,10)).format('DD/MM/YYYY') : '-' },
    { title: 'Próx. vencimento', dataIndex: 'proxima_inspecao_externa', width: 140, render: (v, r) => {
      const s = STATUS_VENCIMENTO(maisProxima(r.proxima_inspecao_externa, r.proxima_inspecao_interna));
      return s ? <Tag color={s.cor}>{s.label}</Tag> : '-';
    }},
    { title: 'Ações', width: 100, render: (_, r) => (
      <Space size={4}>
        {!(r.nome || '').match(/\.docx?$/i) && <Tooltip title="Ver"><Button size="small" icon={<EyeOutlined />} onClick={() => abrirArquivo(r.caminho)} /></Tooltip>}
        <Tooltip title="Baixar"><Button size="small" icon={<DownloadOutlined />} onClick={() => baixarArquivo(r.caminho, r.nome)} /></Tooltip>
      </Space>
    )},
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* Header */}
      <div style={{ background: '#001529', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src="/logo-menu.png" alt="Celum" style={{ height: 36 }} />
          <div style={{ color: '#aaa', fontSize: 13 }}>Portal do Cliente</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#fff', fontSize: 13 }}>{nome}</span>
          <Button size="small" icon={<LogoutOutlined />} onClick={sair} style={{ color: '#aaa', borderColor: '#333' }}>Sair</Button>
        </div>
      </div>

      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', marginTop: 60 }} /> : (
          <>
            {/* ARTs: a do cliente e a de cada CNPJ do laudo diferente */}
            {arts.map((a) => (
              <Card key={a.id} size="small" style={{ marginBottom: 16, borderLeft: '4px solid #1890ff' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <strong>{a.origem === 'cliente' ? 'ART vigente' : 'ART do CNPJ do laudo'}</strong>
                    <div style={{ fontSize: 12, color: '#888' }}>CREA-MG Nº {a.art_numero || '-'}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>{a.razao_social} — {a.cnpj}</div>
                  </div>
                  <Space>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => abrirArquivo(a.art_caminho)}>Ver</Button>
                    <Button size="small" icon={<DownloadOutlined />} onClick={() => baixarArquivo(a.art_caminho, 'ART-' + String(a.cnpj || a.id).replace(/\D/g, '') + '.pdf')}>Baixar</Button>
                  </Space>
                </div>
              </Card>
            ))}
            {/* ART (formato antigo, so se a lista nova nao vier) */}
            {!arts.length && art && (
              <Card size="small" style={{ marginBottom: 16, borderLeft: '4px solid #1890ff' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <strong>ART vigente</strong>
                    <div style={{ fontSize: 12, color: '#888' }}>CREA-MG Nº {art.art_numero}</div>
                  </div>
                  <Space>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => abrirArquivo(art.art_caminho)}>Ver</Button>
                    <Button size="small" icon={<DownloadOutlined />} onClick={() => baixarArquivo(art.art_caminho, 'ART.pdf')}>Baixar</Button>
                  </Space>
                </div>
              </Card>
            )}

            {certificadosPadrao.length > 0 && (
              <Card size="small" style={{ marginBottom: 16, borderLeft: '4px solid #52c41a' }}>
                <div style={{ marginBottom: 8 }}><strong>Certificados dos Padrões de Calibração</strong></div>
                <Space wrap>
                  {certificadosPadrao.map((cert, i) => (
                    <Space key={i} size={4}>
                      <span style={{ fontSize: 13 }}>{cert.nome}</span>
                      <Button size="small" icon={<EyeOutlined />} onClick={() => abrirArquivo(cert.caminho)}>Ver</Button>
                      <Button size="small" icon={<DownloadOutlined />} onClick={() => baixarArquivo(cert.caminho, cert.nome + cert.ext)}>Baixar</Button>
                    </Space>
                  ))}
                </Space>
              </Card>
            )}

            <Tabs defaultActiveKey="pastas">
              <Tabs.TabPane tab={<><FolderOutlined /> Pastas por equipamento</>} key="pastas">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12, marginBottom: 24 }}>
                  {tags.map(tag => (
                    <Card key={tag} size="small" hoverable style={{ textAlign: 'center', cursor: 'pointer', border: tagAberta === tag ? '2px solid #1890ff' : undefined }}
                      onClick={() => setTagAberta(tagAberta === tag ? null : tag)}>
                      <FolderOutlined style={{ fontSize: 28, color: '#faad14' }} />
                      <div style={{ fontWeight: 500, marginTop: 6, fontSize: 13 }}>{tag}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{porTag[tag].length} laudo(s)</div>
                    </Card>
                  ))}
                </div>

                {tagAberta && porTag[tagAberta] && (
                  <Card title={<><FolderOutlined style={{ color: '#faad14' }} /> {tagAberta}</>} size="small">
                    {porTag[tagAberta].map(laudo => {
                      const arqs = laudo.arquivos || [];
                      const vencProx = STATUS_VENCIMENTO(maisProxima(laudo.proxima_inspecao_externa, laudo.proxima_inspecao_interna));
                      const caminhos = laudo.caminhos || [];
                      return (
                        <div key={laudo.id} style={{ padding: '10px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                          <div style={{ marginBottom: 6 }}>
                            <Tag color="blue">{{ inspecao: 'Laudo de Inspeção', cal_valvula: 'Calib. Válvula', cal_manometro: 'Calib. Manômetro', cal_pressostato: 'Calib. Pressostato', cal_transdutor: 'Calib. Transdutor', cal_pneu: 'Calib. Pneu', cal_termometro: 'Calib. Termômetro', prontuario: 'Prontuário', livro: 'Livro de Registro' }[laudo.tipo_laudo] || laudo.tipo_laudo}</Tag>
                            {vencProx && <Tag color={vencProx.cor}>Próxima inspeção: {vencProx.label}</Tag>}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {caminhos.map((arq, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', background: '#f9f9f9', borderRadius: 6 }}>
                                <span style={{ fontSize: 12, color: '#555' }}>📄 {arq.tipo}</span>
                                <Space size={4}>
                                  {!(arq.nome || '').match(/\.docx?$/i) && <Button size="small" icon={<EyeOutlined />} onClick={() => abrirArquivo(arq.caminho)}>Ver</Button>}
                                  <Button size="small" icon={<DownloadOutlined />} onClick={() => baixarArquivo(arq.caminho, arq.nome)}>Baixar</Button>
                                </Space>
                              </div>
                            ))}
                            {caminhos.length === 0 && <span style={{ fontSize: 12, color: '#aaa' }}>Nenhum arquivo disponível</span>}
                          </div>
                        </div>
                      );
                    })}
                  </Card>
                )}
              </Tabs.TabPane>

              <Tabs.TabPane tab={<><FileTextOutlined /> Todos os laudos</>} key="todos">
                <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Input.Search placeholder="Buscar por TAG ou tipo..." value={busca} onChange={e => setBusca(e.target.value)} style={{ width: 300 }} />
                  {selecionados.length > 0 && (
                    <Button icon={<DownloadOutlined />} type="primary" loading={baixando}
                      onClick={() => {
                        const arqs = selecionados.map(key => arquivosFiltrados.find(a => a.key === key)).filter(Boolean);
                        baixarEmMassa(arqs);
                      }}>
                      Baixar selecionados ({selecionados.length}) {baixando && progresso > 0 ? '— ' + progresso + ' feitos' : ''}
                    </Button>
                  )}
                  <Button icon={<DownloadOutlined />} loading={baixando}
                    onClick={() => {
                      const arqs = arquivosFiltrados;
                      baixarEmMassa(arqs);
                    }}>
                    Baixar tudo {baixando && progresso > 0 ? '(' + progresso + '/' + laudosFiltrados.flatMap(l => l.caminhos||[]).length + ')' : ''}
                  </Button>
                  {selecionados.length > 0 && <Button size="small" onClick={() => setSelecionados([])}>Limpar seleção</Button>}
                </div>
                <Table columns={colunasArquivos} dataSource={arquivosFiltrados} rowKey="key" size="small"
                  rowSelection={{ selectedRowKeys: selecionados, onChange: setSelecionados }}
                  locale={{ emptyText: 'Nenhum documento disponível.' }} scroll={{ x: 800 }} />
              </Tabs.TabPane>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}

export default Portal;
